import { AdmissionDeniedError, admissionRedirectFor } from '../lib/admission-redirect';
import { apiFetch, apiUrl, authHeaders } from '../lib/apiFetch';

import type { VenueDetail } from './venues';

/** The crop area in pixels of the photo as it looks on screen (after phone rotation). */
export interface PhotoCropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * A photo request the server (or the network) turned down. `message` is safe to show as is.
 * `aboutFile` is true when picking a different file could fix it.
 */
export class VenuePhotoRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly aboutFile: boolean,
  ) {
    super(message);
    this.name = 'VenuePhotoRequestError';
  }
}

/** Thrown when the caller cancelled the upload on purpose. */
export class PhotoUploadCancelled extends Error {
  constructor() {
    super('Upload cancelled');
    this.name = 'PhotoUploadCancelled';
  }
}

const UPLOAD_TIMEOUT_MS = 120_000;

const photoPath = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}/photo`;

/** Statuses whose server message is written for people and shown word for word. */
const SHOWN_AS_SENT = new Set([400, 403, 404, 503]);
const FALLBACK_MESSAGES: Record<number, string> = {
  403: "You don't have permission to change this venue's photo.",
  404: "That venue couldn't be found. Refresh the page and try again.",
  503: "Photo uploads aren't available right now. Try again later or ask an Admin.",
};
const GENERIC_MESSAGE = 'Something went wrong with the photo. Try again.';
const NETWORK_MESSAGE = "The upload didn't finish. Check your connection and try again.";
const TIMEOUT_MESSAGE = "The upload took too long and didn't finish. Try again.";

/** Only a 400 is about the file itself; a permission or outage refusal is not. */
function refusal(status: number, body: unknown): VenuePhotoRequestError {
  const sent = (body as { message?: unknown } | null)?.message;
  const usable = SHOWN_AS_SENT.has(status) && typeof sent === 'string' && sent.trim();
  const message = usable ? sent : (FALLBACK_MESSAGES[status] ?? GENERIC_MESSAGE);
  return new VenuePhotoRequestError(message, status, status === 400);
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** A venue from a reply body, or null when the body is empty or not a venue. */
function venueFrom(body: unknown): VenueDetail | null {
  return typeof body === 'object' && body !== null && 'id' in body ? (body as VenueDetail) : null;
}

function buildForm(file: File, crop?: PhotoCropArea): FormData {
  const form = new FormData();
  form.append('photo', file);
  if (crop) {
    form.append('cropX', String(crop.x));
    form.append('cropY', String(crop.y));
    form.append('cropWidth', String(crop.width));
    form.append('cropHeight', String(crop.height));
  }
  return form;
}

/**
 * Uploads a venue's booking photo. Resolves with the updated venue, or null when the upload
 * worked but the reply could not be read (the caller should fetch the venue again).
 * Uses XMLHttpRequest because `fetch` cannot report how much of a file has been sent.
 */
export async function uploadVenuePhoto(
  venueId: string,
  file: File,
  crop: PhotoCropArea | undefined,
  onProgress: (percent: number) => void,
  signal?: AbortSignal,
): Promise<VenueDetail | null> {
  const headers = await authHeaders();
  return new Promise<VenueDetail | null>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new PhotoUploadCancelled());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', apiUrl(photoPath(venueId)));
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.min(100, Math.floor((event.loaded / event.total) * 100)));
      }
    };
    xhr.onerror = () => reject(new VenuePhotoRequestError(NETWORK_MESSAGE, 0, true));
    xhr.ontimeout = () => reject(new VenuePhotoRequestError(TIMEOUT_MESSAGE, 0, true));
    xhr.onabort = () =>
      reject(
        signal?.aborted
          ? new PhotoUploadCancelled()
          : new VenuePhotoRequestError(NETWORK_MESSAGE, 0, true),
      );
    xhr.onload = () => {
      const body = parseJson(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(venueFrom(body));
        return;
      }
      const redirect = admissionRedirectFor(xhr.status, body, window.location.pathname);
      if (redirect) window.location.assign(redirect.href);
      reject(refusal(xhr.status, body));
    };
    xhr.send(buildForm(file, crop));
  });
}

/** Removes the uploaded booking photo. Resolves like `uploadVenuePhoto`. */
export async function removeVenuePhoto(venueId: string): Promise<VenueDetail | null> {
  let res: Response;
  try {
    res = await apiFetch(photoPath(venueId), { method: 'DELETE' });
  } catch (error) {
    if (error instanceof AdmissionDeniedError) throw error;
    throw new VenuePhotoRequestError(NETWORK_MESSAGE, 0, false);
  }
  const body = await res.json().catch(() => null);
  if (!res.ok) throw refusal(res.status, body);
  return venueFrom(body);
}
